import {
  BlockStack,
  Checkbox,
  InlineStack,
  Select,
  TextField,
} from '@shopify/polaris';
import {
  DEFAULT_TEXT_STYLE,
  normalizeTextStyle,
} from '../../utils/textStyleLayout';

const FONT_STYLE_OPTIONS = [
  { label: 'Regular', value: 'regular' },
  { label: 'Italic', value: 'italic' },
  { label: 'Bold', value: 'bold' },
  { label: 'Bold Italic', value: 'bold_italic' },
];

const TEXT_ALIGN_OPTIONS = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
];

const LAYOUT_MODE_OPTIONS = [
  { label: 'Exact X/Y position', value: 'absolute' },
  { label: 'Align inside frame', value: 'frame' },
];

const VERTICAL_ALIGN_OPTIONS = [
  { label: 'Top', value: 'top' },
  { label: 'Center', value: 'center' },
  { label: 'Bottom', value: 'bottom' },
];

const HORIZONTAL_ALIGN_OPTIONS = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
];

const fieldContainerStyle = { minWidth: 150, flex: 1 };

const TextStyleEditor = ({
  value,
  onChange,
  fontOptions = [],
  disabled = false,
  isLoadingFonts = false,
}) => {
  const source = value && typeof value === 'object' ? value : DEFAULT_TEXT_STYLE;
  const style = normalizeTextStyle(source);
  const update = (field, nextValue) => onChange({ ...source, [field]: nextValue });
  const numericValue = (field) => (
    typeof source[field] === 'string'
      ? source[field]
      : (style[field] === null ? '' : String(style[field]))
  );
  const commitNumericValue = (field) => {
    if (typeof source[field] !== 'string') return;
    const normalized = normalizeTextStyle(source);
    onChange({ ...source, [field]: normalized[field] });
  };

  return (
    <BlockStack gap="300">
      <InlineStack gap="300" wrap>
        <div style={{ ...fieldContainerStyle, minWidth: 220 }}>
          <Select
            label="Layout"
            options={LAYOUT_MODE_OPTIONS}
            value={style.layout_mode}
            onChange={(nextValue) => update('layout_mode', nextValue)}
            disabled={disabled}
          />
        </div>
        <div style={{ ...fieldContainerStyle, minWidth: 220 }}>
          <Select
            label="Font"
            options={fontOptions}
            value={style.font_key}
            onChange={(nextValue) => update('font_key', nextValue)}
            disabled={disabled}
            helpText={isLoadingFonts ? 'Loading font library...' : 'Empty uses the language fallback font.'}
          />
        </div>
      </InlineStack>

      <InlineStack gap="300" wrap>
        <div style={fieldContainerStyle}>
          <TextField
            label="Font size"
            type="number"
            value={numericValue('font_size')}
            onChange={(nextValue) => update('font_size', nextValue)}
            onBlur={() => commitNumericValue('font_size')}
            disabled={disabled}
            autoComplete="off"
            placeholder="Auto"
          />
        </div>
        <div style={fieldContainerStyle}>
          <Select
            label="Font style"
            options={FONT_STYLE_OPTIONS}
            value={style.font_style}
            onChange={(nextValue) => update('font_style', nextValue)}
            disabled={disabled}
          />
        </div>
        <div style={fieldContainerStyle}>
          <Select
            label="Text alignment"
            options={TEXT_ALIGN_OPTIONS}
            value={style.text_align}
            onChange={(nextValue) => update('text_align', nextValue)}
            disabled={disabled}
          />
        </div>
      </InlineStack>

      {style.layout_mode === 'absolute' ? (
        <InlineStack gap="300" wrap>
          <div style={fieldContainerStyle}>
            <TextField
              label="Text X"
              type="number"
              value={numericValue('x')}
              onChange={(nextValue) => update('x', nextValue)}
              onBlur={() => commitNumericValue('x')}
              disabled={disabled}
              autoComplete="off"
              placeholder="Centered"
            />
          </div>
          <div style={fieldContainerStyle}>
            <TextField
              label="Text Y"
              type="number"
              value={numericValue('y')}
              onChange={(nextValue) => update('y', nextValue)}
              onBlur={() => commitNumericValue('y')}
              disabled={disabled}
              autoComplete="off"
              placeholder="Centered"
            />
          </div>
        </InlineStack>
      ) : (
        <BlockStack gap="300">
          <InlineStack gap="300" wrap>
            <div style={fieldContainerStyle}>
              <Select
                label="Vertical alignment"
                options={VERTICAL_ALIGN_OPTIONS}
                value={style.vertical_align}
                onChange={(nextValue) => update('vertical_align', nextValue)}
                disabled={disabled}
              />
            </div>
            <div style={fieldContainerStyle}>
              <Select
                label="Horizontal alignment"
                options={HORIZONTAL_ALIGN_OPTIONS}
                value={style.horizontal_align}
                onChange={(nextValue) => update('horizontal_align', nextValue)}
                disabled={disabled}
              />
            </div>
            <div style={fieldContainerStyle}>
              <TextField
                label="Frame width (%)"
                type="number"
                min={10}
                max={100}
                value={numericValue('frame_width_percent')}
                onChange={(nextValue) => update('frame_width_percent', nextValue)}
                onBlur={() => commitNumericValue('frame_width_percent')}
                disabled={disabled}
                autoComplete="off"
              />
            </div>
          </InlineStack>

          <InlineStack gap="300" wrap>
            {[
              ['frame_margin_top', 'Top margin'],
              ['frame_margin_right', 'Right margin'],
              ['frame_margin_bottom', 'Bottom margin'],
              ['frame_margin_left', 'Left margin'],
            ].map(([field, label]) => (
              <div style={fieldContainerStyle} key={field}>
                <TextField
                  label={label}
                  type="number"
                  min={0}
                  value={numericValue(field)}
                  onChange={(nextValue) => update(field, nextValue)}
                  onBlur={() => commitNumericValue(field)}
                  disabled={disabled}
                  autoComplete="off"
                />
              </div>
            ))}
          </InlineStack>

          <InlineStack gap="300" wrap blockAlign="center">
            <Checkbox
              label="Auto-shrink text to fit"
              checked={style.auto_fit}
              onChange={(checked) => update('auto_fit', checked)}
              disabled={disabled}
            />
            <div style={fieldContainerStyle}>
              <TextField
                label="Minimum font size"
                type="number"
                min={1}
                value={numericValue('min_font_size')}
                onChange={(nextValue) => update('min_font_size', nextValue)}
                onBlur={() => commitNumericValue('min_font_size')}
                disabled={disabled || !style.auto_fit}
                autoComplete="off"
              />
            </div>
          </InlineStack>
        </BlockStack>
      )}
    </BlockStack>
  );
};

export default TextStyleEditor;
